// FUTURE(NEX-???): When the backend stores parts natively, each Message row will carry
// a `parts: ContentPart[]` field in its JSON content blob:
//   message.content = { text: "", parts: [...] }
// The toContentParts() mapper in toContentParts.ts then becomes:
//   return message.content.parts ?? legacyToContentParts(message)
import { z } from "zod";

// The persisted tool result composes the wire's own dispatch shape rather than
// restating it: the value stored IS the value that crossed, and a second
// declaration would drift from it. This is the one import running from the chat
// kernel into the streaming kernel — the other edges between them run the other
// way — so a change made for the wire's benefit lands here too. That is intended
// for the shape, and has a cost on the arms' strictness, which is a boundary
// property this file does not want: hence the `.catch` on the field below.
import {
  type AgentDispatchResult,
  AgentDispatchResultSchema
} from "../../streaming/agent-dispatch-result";
import type { AgentColourName } from "./agent-colour.schema";
import type { CueErrorKind } from "./ordered-part.schema";
import type { ProviderMetadata } from "./provider-metadata.schema";

/** Base states shared by all content parts */
type ContentPartState = "streaming" | "complete" | "error" | "skipped";

/**
 * Persisted pipeline position of a tool call. All four states survive in the
 * message store / DB. `awaiting-approval` and `rejected` are NOT states here —
 * they derive from `state` plus the live pending-approval set and the result
 * payload. The frontend-only `"executing"` phase (tool approved, not yet settled)
 * is derived from `input-available` + pending-set removal in `toolCallPhase()`;
 * it never appears in persisted rows or on the wire.
 *
 * Wire-derived vs frontend-derived:
 * - `input-streaming` ← `tool-input-start` (call announced, args still streaming).
 * - `input-available` ← `tool-input-available` (args fully parsed).
 * - `output-available` / `output-error` ← `tool-output-available`, split on
 *   `status`/`isError` (accepted → `output-available`; accepted-but-`isError` →
 *   `output-error`; `rejected` is not a persisted state — it's a distinct FE flow).
 * - The transient "executing" phase is FRONTEND-derived only — computed from
 *   `input-available` + no output yet — and never appears as a wire or DB state.
 */
export type InFlightToolCallPartState =
  | "input-streaming"
  | "input-available"
  | "output-available"
  | "output-error";

/** Shared envelope for every content part */
interface BaseContentPart {
  readonly id: string;
  readonly state: ContentPartState;
}

export interface TextPart extends BaseContentPart {
  readonly type: "text";
  readonly content: string;
  /** Non-null when this part was forwarded from a sub-agent. */
  readonly parentToolUseId?: string | null;
}

export interface ReasoningPart extends BaseContentPart {
  readonly type: "reasoning";
  readonly content: string;
  readonly durationMs: number | undefined;
  /** Non-null when this part was forwarded from a sub-agent. */
  readonly parentToolUseId?: string | null;
}

/**
 * Settled result of a tool call. Mirrors the wire `ToolResultEvent` (minus the
 * event discriminant) and is the shape persisted in the TOOL row JSONB.
 *
 * - `status` — the decision: `accepted` (ran) or `rejected` (declined/cancelled).
 * - `output` — present when accepted: the tool's stringified result.
 * - `isError` — accepted-but-failed: applying the result threw.
 * - `reason` — present when rejected: the single human-readable string the model
 *   reads next turn and the user sees in the UI.
 * - `agentResult` — the structured result of an Agent dispatch, carried into the
 *   persisted row so a settled call read back from storage still holds it.
 *
 * The facets are independent — no nested discriminated union.
 */
export interface ToolResult {
  readonly status: "accepted" | "rejected";
  readonly output?: string;
  readonly isError?: boolean;
  readonly reason?: string;
  /**
   * The subagent's report and the run's totals, for an Agent dispatch. Shape and
   * rationale: {@link AgentDispatchResultSchema}.
   *
   * Carried only for an Agent dispatch settled over the v2 transport. Every other
   * tool call, a v1 session, and every row written before this landed carry
   * nothing — there is no backfill, because the value lived in a sandbox that is
   * gone.
   *
   * **Read it as a value, not as a key.** It is never an empty object, so a value
   * that is there is a dispatch that reported something. But "the key is absent"
   * and "the value is `undefined`" are not the same test: parsing a row through
   * {@link ToolResultSchema} sets the key whenever the input had one, so a
   * persisted value matching neither arm comes back as a present key holding
   * `undefined` rather than as no key at all. Branch on the value; `in` and
   * `hasOwnProperty` answer a different question than the one you mean.
   *
   * Both arms persist. The `async_launched` arm's facts look like they are
   * already held by the background-task listing, but that listing is live state:
   * it is empty once nothing is running, so after a reload it restates nothing
   * and this field is the only record that the dispatch was launched at all.
   */
  readonly agentResult?: AgentDispatchResult;
}

/**
 * Zod schema for {@link ToolResult}. Mirrors the interface exactly so the FE
 * can parse the persisted TOOL-row content returned by the decline endpoint's
 * skipped-duplicate path without hand-rolling the validation.
 */
export const ToolResultSchema = z.object({
  status: z.enum(["accepted", "rejected"]),
  output: z.string().optional(),
  isError: z.boolean().optional(),
  reason: z.string().optional(),
  /**
   * See {@link ToolResult.agentResult} — present only for an Agent dispatch.
   *
   * A malformed value costs the FIELD, never the row. Both arms are `.strict()`,
   * so without this a persisted value matching neither arm would fail the whole
   * parse — and the readers of this column drop the row on a failed parse, taking
   * a settled call's `status` and `output` with it. Before the field was declared
   * the same value was silently stripped as an undeclared key and the row parsed,
   * so this restores that tolerance now that the key is named.
   */
  agentResult: AgentDispatchResultSchema.optional().catch(undefined)
});

export interface InFlightToolCallPart extends Omit<BaseContentPart, "state"> {
  readonly type: "tool_call";
  readonly state: InFlightToolCallPartState;
  readonly toolCallId: string;
  readonly toolName: string;
  readonly args: Record<string, unknown>;
  readonly result: ToolResult | undefined;
  /** Raw argument JSON accumulated from `tool-input-delta` chunks — COSMETIC, display-only, never parsed. The parsed form lives in `args` (from `tool-input-available`). */
  readonly argsText?: string;
  /** Non-null when this tool call was made by a subagent spawned via the Agent tool. */
  readonly parentToolUseId?: string | null;
  /** True when the dispatch carried run_in_background (Agent/Task or any backgroundable tool). Sourced from the typed tool-input-available wire field, not the args bag. */
  readonly runInBackground?: boolean;
  /**
   * The colour the dispatched agent's type declares, for an Agent dispatch
   * whose definition declares one. Sourced from the typed wire field and from
   * the persisted part on replay — never from the args bag, which carries only
   * what the model wrote. Absent for every other tool call and for an agent
   * type that declares no colour, which is the ordinary case.
   */
  readonly agentColour?: AgentColourName;
}

export interface FilePart extends BaseContentPart {
  readonly type: "file";
  readonly name: string;
  readonly fileId: string;
  readonly downloadUrl: string;
  readonly publicUrl: string | undefined;
  /**
   * IANA media type of the file (e.g. `image/png`, `text/csv`). Carried by the
   * live `file` SSE event and the persisted `codeInterpreterFiles` snapshot.
   * Optional: messages that predate the field omit it.
   */
  readonly mediaType?: string;
}

export interface ImagePart extends BaseContentPart {
  readonly type: "image";
  readonly url: string;
  readonly alt: string | undefined;
}

export interface SourcePart extends BaseContentPart {
  readonly type: "source";
  readonly title: string;
  readonly url: string | undefined;
  readonly snippet: string | undefined;
}

/**
 * Where a classified failure reached the reader. `turn`: it ended a turn the
 * reader can see — one streaming, or the stored row a reopened conversation
 * draws it on. `between-turns`: it came from work the lead started on its own
 * between two messages, before that work had drawn anything, so no turn on
 * screen stopped.
 */
export type FailurePlace = "turn" | "between-turns";

export interface ErrorPart extends BaseContentPart {
  readonly type: "error";
  readonly message: string;
  readonly details: string | undefined;
  /**
   * The kind the failure was classified as. Present on a provider failure a
   * Cue turn persisted; absent on every other error part — the legacy chat's
   * `Message processing failed` part carries none.
   */
  readonly errorKind?: CueErrorKind;
  /**
   * Where the failure reached the reader. Absent reads as `turn` — every
   * error part a turn's own row carries, which is every error part before
   * this field existed.
   */
  readonly place?: FailurePlace;
}

/**
 * A message a background agent sent the lead, rendered where it was delivered — it takes
 * its slot among the turn's other parts, so part order alone orders the
 * transcript and no second list is needed to interleave it.
 *
 * `senderName` is a display label the sender asserted about itself.
 * `senderTaskId` is the identity that joins the message to its listing row and
 * its thread. `senderSummary` is the sender's own recap of what it wrote; it rides
 * the send rather than the delivery, so it arrives under the same condition
 * `senderTaskId` does and its absence is never evidence none was written.
 *
 * No surface draws `senderSummary` today. It is carried because a row written before
 * a field existed carries it never — the value lived in a sandbox that is gone,
 * so deferring the field would leave a permanent hole rather than deferring work.
 */
export interface PeerMessagePart extends BaseContentPart {
  readonly type: "peer_message";
  readonly body: string;
  readonly senderName?: string;
  readonly senderTaskId?: string;
  readonly senderSummary?: string;
}

/**
 * The `kind` an agent-message row carried in the content blob before this
 * delivery took the SDK's own word for it. Persisted rows are never rewritten,
 * so a conversation opened today reads rows holding this value beside rows
 * holding the current one; both readers of that blob — Cue's
 * `agentMessageRowContentSchema` and the panel's own row adapter — accept it,
 * and neither branches on which it found. Writers emit only `"peer_message"`.
 */
export const LEGACY_PEER_MESSAGE_ROW_KIND = "teammate_message";

/**
 * One SDK signal drawn where it arrived, with no rendering designed for it.
 *
 * `signal` is the SDK's own words for what fired, namespaced by the message that
 * carried it (`system/<a subtype this build has never seen>`); `payload` is the
 * signal's own shape, opaque to everything between the runner and the row that
 * draws it.
 *
 * A signal is durable, and it is durable as a row of its own rather than as one
 * of a turn's ordered parts. It belongs to the run rather than to any turn — the
 * between-turns feed delivers these with nothing open — so a conversation
 * reloaded from its rows rebuilds each signal from the row the materializer
 * wrote, into a part carrying the same identity its live arrival carried.
 *
 * This part therefore never appears inside `parts`. Its place there would be a
 * second identity for one signal, and the reader's id-keyed merge would draw
 * both.
 */
export interface GenericSignalPart extends BaseContentPart {
  readonly type: "generic_signal";
  readonly signal: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

/**
 * A provider content block this build does not model, drawn where it arrived.
 *
 * The browser is handed the block verbatim and reads nothing out of it but the
 * label: `providerMetadata` is the part's content rather than a decoration on
 * it, which is why it is required here and optional on every modelled part. It
 * is the same bag the persisted `unknown` ordered part carries, so the live
 * arrival and the reload build this part from identical input and neither side
 * has to know which provider produced it.
 *
 * A content block, not a signal: it belongs to the turn that emitted it and
 * takes its slot among that turn's other parts, so stream order draws it in
 * place. `parentToolUseId` is set when a sub-agent emitted it, which is what
 * partitions it onto that agent's own surface.
 *
 * Deliberately without an opinion. What a given block should look like — an MCP
 * call laid out as a call, a redacted thinking block drawn as a gap — is a
 * rendering decision per block type, and this part is what makes the block
 * visible before anyone has taken one. Redacted reasoning is typed on both wire
 * hops and still lands here, because the fact it carries is the block itself and
 * that is what reconstruction re-emits; only its rendering is still open.
 */
export interface UnknownBlockPart extends BaseContentPart {
  readonly type: "unknown_block";
  readonly providerMetadata: ProviderMetadata;
  /** Non-null when a sub-agent emitted this block. */
  readonly parentToolUseId?: string | null;
}

export type ContentPart =
  | TextPart
  | ReasoningPart
  | InFlightToolCallPart
  | FilePart
  | ImagePart
  | SourcePart
  | ErrorPart
  | PeerMessagePart
  | GenericSignalPart
  | UnknownBlockPart;

export type ContentPartType =
  | "text"
  | "reasoning"
  | "tool_call"
  | "file"
  | "image"
  | "source"
  | "error"
  | "peer_message"
  | "generic_signal"
  | "unknown_block";

export function isTextPart(part: ContentPart): part is TextPart {
  return part.type === "text";
}

export function isInFlightToolCallPart(part: ContentPart): part is InFlightToolCallPart {
  return part.type === "tool_call";
}

/**
 * Derives the result-level status from the tool-call lifecycle `state`, the
 * single source of truth. Non-terminal states (`input-streaming`, `input-available`)
 * → `"pending"`; `output-available` → `"done"`; `output-error` → `"failed"`.
 */
export function toolCallStatus(state: InFlightToolCallPartState): "pending" | "done" | "failed" {
  if (state === "output-available") return "done";
  if (state === "output-error") return "failed";
  return "pending";
}
